/**
 * "No Recruiter id" → paste the Recruiter profile link. Without the id, "Open composer" can only
 * open the public profile; the id comes from the "Save to PDF" file name, which a single or renamed
 * PDF does not carry (and the PDF itself never does).
 */
import { useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate } from '../../types/taOutreach';

export function RecruiterLinkFix({ candidate, onSaved }: { candidate: OutreachCandidate; onSaved: (item: OutreachCandidate) => void }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  if (candidate.recruiterId) return null;
  const save = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      const { item } = await api.setRecruiterLink(candidate.id, value);
      onSaved(item);
      setValue('');
      showToast({ status: 'success', description: `Recruiter link saved — the composer opens directly for ${candidate.name} now.` });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not save that link.' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-3 py-2 text-[13px] text-[#7A4B00]">
      <b>No Recruiter id</b> — the button can only open the public profile. Open {candidate.name} in LinkedIn Recruiter, copy the address
      bar (…/talent/profile/AEMAA…) and paste it here:
      <div className="mt-1.5 flex gap-2">
        <input
          className="h-8 min-w-0 flex-1 rounded-md border border-solid border-[#E9EAEC] bg-white px-2 text-[#1A1A1A]"
          placeholder="https://www.linkedin.com/talent/profile/AEMAA…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void save()}
          aria-label={`Recruiter link for ${candidate.name}`}
        />
        <button
          type="button"
          disabled={busy || !value.trim()}
          onClick={() => void save()}
          className="h-8 rounded-md bg-[#0072EA] px-3.5 text-xs font-semibold text-white hover:bg-[#0167D4] disabled:opacity-40"
        >
          Save link
        </button>
      </div>
    </div>
  );
}
